// SecondaryDock — the right zone (ONE SecondaryPane for every dock slot).
//
// Extracted from AppShellHost's render tree (C1 #9). Its head is DEMOTED
// (composition contract D2): it names the slot and closes — switching slots is
// the ribbon's job alone, so the two can never disagree (V3).
//
//   assistant — the chat itself, docked beside the main view (redesign step
//               1, rightPanel/ChatPanelHost: tabs Assistant | Context);
//   panel     — the chat's Context tab (rightPanel/ContextPanel): the
//               material made here under its two lenses, the sources and
//               where they are used, other files;
//   review    — a material's proposed changes;
//   grounding — a material's terse quality glance.
// Review + Quality are MATERIAL-scoped (right-dock-design §4): they follow the
// focused pane's active material (the contextual jumps still summon them); away
// from a material tab they show a calm empty state, never stale content
// masquerading as current.

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { SecondarySlot } from "../../components/AppShell/AppShell.js";
import { ProposalReview } from "../../components/Editor/ProposalReview/ProposalReview.js";
import { EmptyState } from "../../components/EmptyState/EmptyState.js";
import { QualitySummary } from "../../components/QualitySummary/QualitySummary.js";
import { SecondaryPane } from "../../components/SecondaryPane/SecondaryPane.js";
import { useAppShell } from "./context.js";
import { ChatPanelHost } from "./rightPanel/ChatPanelHost.js";
import "./SecondaryDock.css";

export const SecondaryDock = (): ReactNode => {
    const { t } = useLingui();
    const {
        frame,
        dispatch,
        editor,
        activeMaterialId,
        activeTitle,
        paneSubject,
        refFor,
        quality,
        policy,
    } = useAppShell();

    // The slot the pane shows. While the dock closes (`openSecondary` null) the
    // pane keeps the last summoned slot's content for the width/opacity
    // collapse, instead of swapping to the empty Review body mid-animation.
    // View-ephemeral: derived from the prop during render, not domain state.
    const [lastSlot, setLastSlot] = useState<SecondarySlot>(
        frame.openSecondary ?? "review",
    );
    if (frame.openSecondary !== null && frame.openSecondary !== lastSlot)
        setLastSlot(frame.openSecondary);
    const slot = frame.openSecondary ?? lastSlot;

    // Closing the pane collapses the right zone (the dock slot only).
    const closeSecondary = () => dispatch({ type: "close-secondary" });

    // Panel subjects (composition contract I6/D7): the pane FOLLOWS the active
    // tab, and whenever what it shows is NOT the active tab the head names the
    // subject so a lingering read never passes as current (V7). The title
    // resolves live (rename-fresh) with the captured title as the fallback.
    const paneSubjectLabel: string | undefined =
        paneSubject !== null && paneSubject.id !== activeMaterialId
            ? (refFor(paneSubject.id)?.title ?? paneSubject.title)
            : undefined;

    // The calm no-material-in-focus body for the two material-scoped panels.
    const noMaterialNode: ReactNode = (
        <EmptyState
            title={t`No material in focus`}
            body={t`Open or focus a material to review its proposals and quality.`}
        />
    );

    return (
        <SecondaryPane
            ariaLabel={t`Secondary`}
            slot={slot}
            subject={paneSubjectLabel}
            onClose={closeSecondary}
            slotContent={{
                grounding:
                    activeMaterialId === null
                        ? { type: "scrolling", node: noMaterialNode }
                        : quality !== null
                          ? {
                                type: "scrolling",
                                node: (
                                    <QualitySummary
                                        ariaLabel={t`Material quality`}
                                        quality={quality}
                                        policy={policy ?? undefined}
                                        onOpenFullQuality={() => {
                                            // Open the full report as its OWN
                                            // tab (owner decision), scoped to
                                            // the active material.
                                            if (activeMaterialId !== null)
                                                dispatch({
                                                    type: "open-quality-report",
                                                    materialId:
                                                        activeMaterialId,
                                                    title:
                                                        activeTitle ||
                                                        activeMaterialId,
                                                });
                                        }}
                                    />
                                ),
                            }
                          : undefined,
                review:
                    activeMaterialId === null
                        ? { type: "scrolling", node: noMaterialNode }
                        : {
                              type: "scrolling",
                              node: (
                                  <ProposalReview
                                      ariaLabel={t`Changes to review`}
                                      proposals={editor.proposals}
                                      onAccept={editor.onAcceptProposal}
                                      onReject={editor.onRejectProposal}
                                      pendingId={
                                          editor.reviewPendingId ?? undefined
                                      }
                                  />
                              ),
                          },
                assistant: {
                    type: "own-head",
                    node: <ChatPanelHost view="assistant" />,
                },
                panel: {
                    type: "own-head",
                    node: <ChatPanelHost view="context" />,
                },
            }}
        />
    );
};
